import type { FileWithPreview } from 'components/FileDragAndDrop';

import {
    CreateMealPlanDefinitionInput,
    MealPlanDefinitionFragmentFragment,
    MealPlanDefinitionState,
    UpdateMealPlanDefinitionInput,
} from 'data/__generated__';

/**
 * Une image du formulaire est soit celle déjà en ligne (`url`), soit un fichier fraîchement déposé
 * (`file` + `preview`). Le `thumbhash` est calculé dans le navigateur au moment du dépôt : voir
 * `helpers/thumbhash`.
 */
export type MealPlanImageField = {
    url: string | null;
    file: FileWithPreview | null;
    preview: string | null;
    thumbhash: string;
};

export type MealPlanDefinitionFormData = {
    name: string;
    /** `[début, fin]`, indexé directement — ce n'est pas une liste, c'est un couple. */
    hoursIntervalAvailable: string[];
    /**
     * Saisi en POURCENTAGE : l'API stocke des ratios (`0.1`), l'exploitation raisonne en « 10 % ».
     * L'indice vaut `nombre de produits - 2` : la première remise s'applique à deux produits.
     *
     * `null` = champ vidé en cours de saisie ; c'est l'API qui refuse une remise hors de `]0;1[`.
     */
    discountRatios: { percentage: number | null }[];
    eligibleCategories: { label: string; categoryIds: string[] }[];
    images: MealPlanImageField[];
    state: MealPlanDefinitionState;
};

export const EMPTY_MEAL_PLAN_DEFINITION: MealPlanDefinitionFormData = {
    name: '',
    hoursIntervalAvailable: ['11:30', '14:30'],
    discountRatios: [{ percentage: 10 }],
    eligibleCategories: [
        { label: '', categoryIds: [] },
        { label: '', categoryIds: [] },
    ],
    images: [],
    state: MealPlanDefinitionState.Regular,
};

/**
 * Ratio ↔ pourcentage, réversible.
 *
 * L'aller-retour passe par des ENTIERS de centièmes de pourcent (`0.125` ⇄ `12,5 %`) : arrondir au
 * pourcent entier faisait qu'ouvrir une formule à 12,5 % et la réenregistrer — même sans toucher à
 * la remise — la ramenait silencieusement à 13 %. L'arrondi reste nécessaire pour absorber le
 * flottant (`0.15 * 10000 === 1500.0000000000002`), mais il ne perd plus de décimale utile.
 */
const PERCENT_SCALE = 10000;

const toPercentage = (ratio: number): number => Math.round(ratio * PERCENT_SCALE) / 100;

const toRatio = (percentage: number | null): number =>
    percentage === null ? 0 : Math.round(percentage * 100) / PERCENT_SCALE;

export const toFormData = (mealPlanDefinition: MealPlanDefinitionFragmentFragment): MealPlanDefinitionFormData => ({
    name: mealPlanDefinition.name,
    hoursIntervalAvailable: [...mealPlanDefinition.hoursIntervalAvailable],
    discountRatios: mealPlanDefinition.discountRatios.map((ratio) => ({ percentage: toPercentage(ratio) })),
    eligibleCategories: mealPlanDefinition.eligibleCategories.map(({ label, categoryIds }) => ({
        label,
        categoryIds: [...categoryIds],
    })),
    images: mealPlanDefinition.images.map(({ url, thumbhash }) => ({ url, file: null, preview: null, thumbhash })),
    state: mealPlanDefinition.state,
});

export const toCreateInput = (values: MealPlanDefinitionFormData): CreateMealPlanDefinitionInput => ({
    name: values.name.trim(),
    hoursIntervalAvailable: values.hoursIntervalAvailable,
    discountRatios: values.discountRatios.map(({ percentage }) => toRatio(percentage)),
    eligibleCategories: values.eligibleCategories.map(({ label, categoryIds }) => ({
        label: label.trim(),
        categoryIds,
    })),
    // `url` et `file` sont exclusifs : un fichier déposé remplace l'image en ligne, et c'est l'API
    // qui le téléverse puis en rend l'URL.
    images: values.images.map(({ url, file, thumbhash }) => ({
        url: file ? null : url,
        file: file ?? null,
        thumbhash,
    })),
});

export const toUpdateInput = (values: MealPlanDefinitionFormData): UpdateMealPlanDefinitionInput => ({
    ...toCreateInput(values),
    state: values.state,
});
